import { supabase } from '@/lib/supabase/client';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export type NoteType = 'admin_note' | 'customer_success' | 'communication';

export type CommsChannel = 'call' | 'email' | 'meeting' | 'in_person' | 'slack' | 'other';

export interface Note {
  id: string;
  location_id: string;
  note_type: NoteType;
  content: string;
  subject: string | null;
  channel: CommsChannel | null;
  promised_date: string | null;
  follow_up_date: string | null;
  author_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface NoteInsert {
  location_id: string;
  note_type: NoteType;
  content: string;
  subject?: string | null;
  channel?: CommsChannel | null;
  promised_date?: string | null;
  follow_up_date?: string | null;
  author_id?: string | null;
}

export interface NoteUpdate {
  note_type?: NoteType;
  content?: string;
  subject?: string | null;
  channel?: CommsChannel | null;
  promised_date?: string | null;
  follow_up_date?: string | null;
}

function normalizeOptionalText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeOptionalDate(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, 10) : null;
}

export const notesService = {
  async listByLocationIds(locationIds: string[]): Promise<Note[]> {
    if (locationIds.length === 0) return [];

    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .in('location_id', locationIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notes by location ids:', error);
        return [];
      }

      return (data as Note[]) || [];
    } catch (error) {
      console.error('Error fetching notes by location ids:', error);
      return [];
    }
  },

  async getByLocationId(locationId: string): Promise<Note[]> {
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notes:', error);
        return [];
      }

      return (data as Note[]) || [];
    } catch (error) {
      console.error('Error fetching notes:', error);
      return [];
    }
  },

  async getById(id: string): Promise<Note | null> {
    try {
      const { data, error } = await supabase.from('notes').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching note:', error);
        return null;
      }

      return (data as Note) || null;
    } catch (error) {
      console.error('Error fetching note:', error);
      return null;
    }
  },

  async create(data: NoteInsert): Promise<Note> {
    try {
      const userId = await getCurrentUserId();

      const insertData = {
        location_id: data.location_id,
        note_type: data.note_type,
        content: data.content,
        subject: normalizeOptionalText(data.subject),
        channel: data.channel ?? null,
        promised_date: normalizeOptionalDate(data.promised_date),
        follow_up_date: normalizeOptionalDate(data.follow_up_date),
        author_id: data.author_id ?? userId,
      };

      const { data: newNote, error } = await supabase.from('notes').insert(insertData).select().single();

      if (error) {
        console.error('Error creating note:', error);
        throw new Error(`Failed to create note: ${error.message}`);
      }

      return newNote as Note;
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  },

  async update(id: string, data: NoteUpdate): Promise<Note> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.note_type !== undefined) updateData.note_type = data.note_type;
      if (data.content !== undefined) updateData.content = data.content;
      if (data.subject !== undefined) updateData.subject = normalizeOptionalText(data.subject);
      if (data.channel !== undefined) updateData.channel = data.channel;
      if (data.promised_date !== undefined)
        updateData.promised_date = normalizeOptionalDate(data.promised_date);
      if (data.follow_up_date !== undefined)
        updateData.follow_up_date = normalizeOptionalDate(data.follow_up_date);

      const { data: updatedNote, error } = await supabase
        .from('notes')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating note:', error);
        throw new Error(`Failed to update note: ${error.message}`);
      }

      if (!updatedNote) {
        throw new Error('Note not found');
      }

      return updatedNote as Note;
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('notes').delete().eq('id', id);

      if (error) {
        console.error('Error deleting note:', error);
        throw new Error(`Failed to delete note: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },
};

